import React from 'react';
import PropTypes from 'prop-types';
import assign from 'lodash/assign';

import ConfirmationModal from 'zignal-landscape/lib/ConfirmationModal';
import TrashIcon from 'zignal-landscape/lib/Icon/TrashIcon';
import EditIcon from 'zignal-landscape/lib/Icon/EditIcon';
import Remove1Icon from 'zignal-landscape/lib/Icon/Remove1Icon';
import Save1Icon from 'zignal-landscape/lib/Icon/Save1Icon';
import Add2Icon from 'zignal-landscape/lib/Icon/Add2Icon';
import ChevronUpIcon from 'zignal-landscape/lib/Icon/ChevronUpIcon';
import ChevronRightIcon from 'zignal-landscape/lib/Icon/ChevronRightIcon';

import Input from 'zignal-landscape/lib/Input';
import Link from 'zignal-landscape/lib/Link';

import WorkbenchValue from './WorkbenchValue';
import WorkbenchValueEditInput from './WorkbenchValueEditInput';
import WorkbenchFieldCampaignSelector from './WorkbenchFieldCampaignSelector';
import styles from './WorkbenchField.css';
import { getFieldTypeForDisplay, truncateString } from './helpers';

import classNames from 'classnames/bind';

class WorkbenchField extends React.Component {
  constructor(props) {
    super(props);

    this._getInitialEditingState = this._getInitialEditingState.bind(this);
    this.closeConfirmationModal = this.closeConfirmationModal.bind(this);
    this.handleConfirmDelete = this.handleConfirmDelete.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleEditCancel = this.handleEditCancel.bind(this);
    this.handleEditApply = this.handleEditApply.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputClick = this.handleInputClick.bind(this);
    this.onAddFieldValue = this.onAddFieldValue.bind(this);
    this.handleAddNewFieldValue = this.handleAddNewFieldValue.bind(this);
    this.toggleExpansionPanel = this.toggleExpansionPanel.bind(this);
    this.handleCampaignSelectorChangeCheckbox = this.handleCampaignSelectorChangeCheckbox.bind(this);
    this.handleCampaignSelectorChangeSelect = this.handleCampaignSelectorChangeSelect.bind(this);

    const {
      field,
      showAddFieldValue, showAddFieldValueButton, isExpansionPanelOpen
    } = props;

    this.state = Object.assign({
      showAddFieldValue,
      showAddFieldValueButton,
      isConfirmationModalOpen: false,
      isExpansionPanelOpen,
    }, this._getInitialEditingState(field));
  }

  onAddFieldValue() {
    this.setState({showAddFieldValue: true, showAddFieldValueButton: false});
  }

  // Must pass in `field` as an argument to this function. We can't just use
  // `this.props.field` because it isn't defined yet during `constructor` call.
  _getInitialEditingState(field) {
    const { name, campaignIds, isAvailableToAllCampaigns } = field;
    return {
      editingFieldName: name,
      editingFieldCampaignIds: campaignIds,
      editingFieldIsAvailableToAllCampaigns: isAvailableToAllCampaigns,
      isEditing: false,
    };
  }

  handleDelete(e) {
    e.stopPropagation();
    this.setState({ isConfirmationModalOpen: true });
  }

  handleEdit(e) {
    e.stopPropagation();
    this.setState({isEditing: true});
  }

  handleEditCancel(e) {
    e.stopPropagation();
    // reset edit-related values
    this.setState(this._getInitialEditingState(this.props.field));
  }

  handleEditApply(e) {
    e.stopPropagation();
    e.preventDefault();
    const {onUpdate, field} = this.props;
    const {editingFieldName, editingFieldCampaignIds, editingFieldIsAvailableToAllCampaigns} = this.state;
    const updatedField = assign({}, field, {
      name: editingFieldName,
      campaignIds: editingFieldCampaignIds,
      isAvailableToAllCampaigns: editingFieldIsAvailableToAllCampaigns,
    });
    onUpdate(updatedField);
    this.setState({isEditing: false});
  }

  handleInputChange(e) {
    e.stopPropagation();
    this.setState({editingFieldName: e.target.value});
  }

  handleInputClick(e) {
    e.stopPropagation();
  }

  handleAddNewFieldValue() {
    this.setState({showAddFieldValue: false, showAddFieldValueButton: true});
  }

  toggleExpansionPanel() {
    this.setState({isExpansionPanelOpen: !this.state.isExpansionPanelOpen});
  }

  closeConfirmationModal() {
    this.setState({ isConfirmationModalOpen: false });
  }

  handleConfirmDelete(e) {
    e.stopPropagation();
    const {
      field,
      onDelete
    } = this.props;
    this.setState({ isConfirmationModalOpen: false });
    onDelete(field._id);
  }

  handleCampaignSelectorChangeCheckbox(event) {
    const { editingFieldCampaignIds } = this.state;
    const checked = event.currentTarget.checked || false;
    this.setState({
      editingFieldIsAvailableToAllCampaigns: checked,
      editingFieldCampaignIds: (checked ? [] : editingFieldCampaignIds),
    });
  }

  handleCampaignSelectorChangeSelect(options) {
    const { editingFieldIsAvailableToAllCampaigns } = this.state;
    const campaignIds = options.map((option) => option.value);
    this.setState({
      editingFieldIsAvailableToAllCampaigns,
      editingFieldCampaignIds: (editingFieldIsAvailableToAllCampaigns ? [] : campaignIds),
    });
  }

  _renderCampaignSelector() {
    const { campaigns } = this.props;
    const {
      editingFieldCampaignIds,
      editingFieldIsAvailableToAllCampaigns,
    } = this.state;

    return (
      <div className={styles.fieldCampaignSelector}>
        <WorkbenchFieldCampaignSelector
          allCampaigns={campaigns}
          campaignIds={editingFieldCampaignIds}
          isAvailableToAllCampaigns={editingFieldIsAvailableToAllCampaigns}
          onChangeCheckbox={this.handleCampaignSelectorChangeCheckbox}
          onChangeSelect={this.handleCampaignSelectorChangeSelect}
        />
      </div>
    );
  }

  render() { // eslint-disable-line complexity
    const {
      field, isAccountLevel,
      onFieldValueUpdate, onFieldValueDelete, onFieldValueAdd,
    } = this.props;
    const {
      isEditing,
      editingFieldName,
      showAddFieldValueButton,
      showAddFieldValue,
      isConfirmationModalOpen,
      isExpansionPanelOpen,
    } = this.state;

    const isShared = ((! isAccountLevel) && field.isAvailableToAccount) || false;
    const hasEditableValues = field.type === 'Categorical' || field.type === 'Ordinal';
    const canEditValues = hasEditableValues && (! isShared);
    const isExpandable = hasEditableValues || field.type === 'Boolean';

    // set all Boolean values to "yes" and "no" when expanded
    // TODO: render actual Boolean values when available?
    if (field.type === 'Boolean') {
      field.values = [{
        _id: field.id + '_trueBoolean',
        name: 'Yes'
      }, {
        _id: field.id + '_falseBoolean',
        name: 'No'
      }];
    }

    let ordinalFieldDescription = null;
    if (field.type === 'Ordinal') {
      ordinalFieldDescription = (<div className={styles.addListItemDescription}>
        For ordinal attribute values, create values in order of increasing
        magnitude. Ex: Low, Medium, High
      </div>);
    }

    // render values for all fields except 'Text'
    let fieldValues;
    if (field.type === 'Text') {
      fieldValues = <div />;
    } else {
      fieldValues = field.values.map((value) =>
        (<WorkbenchValue
          key={value._id}
          field={field}
          value={value}
          isShared={isShared}
          onUpdate={onFieldValueUpdate}
          onDelete={onFieldValueDelete}
        />)
      );
    }

    let addFieldValueItem = null;
    if(showAddFieldValueButton) {
      addFieldValueItem =(
        <li className={styles.addListItem}>
          <Link
            icon={<Add2Icon />}
            onClick={this.onAddFieldValue}
          >
            Add Value
          </Link>
          {ordinalFieldDescription}
        </li>
      );
    } else if (showAddFieldValue) {
      addFieldValueItem = (
        <li className={styles.newListItem}>
          <WorkbenchValueEditInput
            field={field}
            onCancel={this.handleAddNewFieldValue}
            onUpdate={onFieldValueAdd}
          />
          {ordinalFieldDescription}
        </li>
      );
    }

    let fieldActions = null;
    if (isShared) {
      fieldActions = (
        <span className={styles.fieldActionShared}>Shared</span>
      );
    }
    else {
      fieldActions = (<span>
        <span className={styles.fieldActionDelete} onClick={this.handleDelete}><TrashIcon /></span>
        <span className={styles.fieldActionEdit} onClick={this.handleEdit}><EditIcon /></span>
      </span>);
    }

    const fieldNameForDisplay = truncateString(field.name, 32);
    const fieldTypeForDisplay = getFieldTypeForDisplay(field.type);
    const fieldMultiLabel = (field.multi
      ? (<span className={styles.fieldMultiLabel}>*Multiple Values Allowed</span>)
      : null
    );

    const fieldLabel = (
      <div className={styles.fieldLabel}>
        <span className={field.type === 'Text' ? styles.fieldNameWithoutIcon : styles.fieldName}>
          {fieldNameForDisplay}
        </span>
        <span className={styles.fieldType}>
          {fieldTypeForDisplay}
        </span>
        {fieldMultiLabel}
        {fieldActions}
      </div>
    );

    // only the Label can be edited in-place
    const editingLabel = (
      <form
        className={styles.form}
        onSubmit={this.handleEditApply}
      >
        <div className={styles.inputFieldName}>
          <Input
            autoFocus
            placeholder="Attribute Name"
            onChange={this.handleInputChange}
            onClick={this.handleInputClick}
            value={editingFieldName || ''}
          />
        </div>
        <span className={styles.fieldType}>{fieldTypeForDisplay}</span>
        {fieldMultiLabel}
        {isAccountLevel ? this._renderCampaignSelector() : null}
        <span className={styles.editActionClose} onClick={this.handleEditCancel}>
          <Remove1Icon width={20} height={30} />
        </span>
        <span
          className={editingFieldName ? styles.editActionCheckValid : styles.editActionCheck}
          onClick={this.handleEditApply}
        >
          <Save1Icon width={20} height={30} />
        </span>
      </form>
    );

    const confirmationModal = (
      <ConfirmationModal
        confirmStyle="delete"
        isOpen={isConfirmationModalOpen}
        onCancel={this.closeConfirmationModal}
        onConfirm={this.handleConfirmDelete}
        title="Delete Attribute"
      >
        <p className={styles.confirmationModalContent}>
          Deleting the <strong>{field.name}</strong> attribute will also delete
          its values. Are you sure you want to delete this attribute?
        </p>
      </ConfirmationModal>
    );

    let icon = null;
    if (isExpandable) {
      icon = isExpansionPanelOpen ? <ChevronUpIcon /> : <ChevronRightIcon />;
    }

    let containerStyles = styles.container;
    if (isAccountLevel) {
      containerStyles = classNames(styles.container, styles.accountContainer);
    }

    return (
      <div className={containerStyles}>
        <div className={styles.fieldHeader} onClick={this.toggleExpansionPanel}>
          {icon} {isEditing ? editingLabel : fieldLabel}
        </div>
        {isExpansionPanelOpen ?
          <ul className={styles.valueList}>
            {fieldValues}
            {canEditValues ? addFieldValueItem : null}
          </ul>
          : null}
        {confirmationModal}
      </div>
    );
  }
}

WorkbenchField.propTypes = {
  field: PropTypes.object.isRequired,
  campaigns: PropTypes.array.isRequired,
  isAccountLevel: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onFieldValueUpdate: PropTypes.func.isRequired,
  onFieldValueAdd: PropTypes.func.isRequired,
  onFieldValueDelete: PropTypes.func.isRequired,
  isExpansionPanelOpen: PropTypes.bool.isRequired,
  showAddFieldValue: PropTypes.bool.isRequired,
  showAddFieldValueButton: PropTypes.bool.isRequired
};

export default WorkbenchField;
